import { QueryClient } from "@tanstack/react-query"
import filter from "lodash-es/filter"
import flatMap from "lodash-es/flatMap"
import groupBy from "lodash-es/groupBy"
import head from "lodash-es/head"
import isEmpty from "lodash-es/isEmpty"
import map from "lodash-es/map"
import values from "lodash-es/values"

import type { TranslationObj } from "$/utils/lang"

import {
    ECoachNavigationStepperBulletCompletion as EStepperBulletCompletion,
    ECoachNavigationStepperBulletOrder as EStepperBulletOrder,
    ECoachNavigationStepperBulletState as EStepperBulletState,
    type TCoachNavigationStepperBullet as TNavigationBullet
} from "@/3514/components"
import { EPath } from "@/3514/constants"
import { coachParticipantModuleByIdQueryPrefetcher as modulePrefetcher } from "@/3514/pages/coach.group-participant/coach.group-participant.api.queries.hooks"
import type {
    ICoachGroupParticipantModuleByIdQueryModel as IModel,
    ICoachGroupParticipantModuleByIdModel as IModuleModel
} from "@/3514/pages/coach.group-participant/coach.group-participant.api.types"
import {
    EChapterComponentType,
    type IChapterBaseModel,
    type IChapterComponentModel,
    type IChapterCoachingMomentComponentModel as IChatComponentModel,
    type IEnrollmentModel
} from "@/3514/types"
import { type TGetValueFromTranslatedObjectFunction, TTFunction } from "@/hooks"

export default {
    getModulesStepperBullets({
        journeyLength,
        enrollments,
        isQuerySuccess,
        moduleId,
        groupId,
        participantId,
        qc,
        translator,
        t
    }: {
        journeyLength: number
        enrollments: IEnrollmentModel[] | []
        isQuerySuccess: boolean
        moduleId: number
        groupId: number
        participantId: number
        qc: QueryClient
        translator: TGetValueFromTranslatedObjectFunction
        t: TTFunction
    }): TNavigationBullet[] {
        const isDataEmpty: boolean = isEmpty(enrollments)
        const modulesToRender: Partial<IEnrollmentModel>[] =
            !isQuerySuccess && isDataEmpty
                ? Array.from({ length: journeyLength }, (_, idx: number) => ({
                      id: -1,
                      status: "queued",
                      name: { en: `Module ${idx + 1}` }
                  }))
                : enrollments

        const adaptedEnrollments: TNavigationBullet[] = map<Partial<IEnrollmentModel>, TNavigationBullet>(
            modulesToRender,
            (m: Partial<IEnrollmentModel>, idx: number): TNavigationBullet => {
                const _isUnassigned: boolean = isDataEmpty ? false : m.status === "unassigned"

                return {
                    async onHover(): Promise<void> {
                        if (_isUnassigned) return

                        await modulePrefetcher({ qc }, { participantId, moduleId: m.id })
                    },
                    href: isDataEmpty
                        ? undefined
                        : EPath.Coach_GroupParticipant(
                              { groupId, participantId },
                              { tab: "Modules", moduleId: _isUnassigned ? -m.rank : m.id }
                          ),
                    pageId: _isUnassigned ? -m.rank : m.id,
                    order: ((): EStepperBulletOrder => {
                        if (isDataEmpty || (moduleId < 0 ? m.rank !== Math.abs(moduleId) : m.id !== moduleId))
                            return EStepperBulletOrder.NotCurrent

                        return EStepperBulletOrder.Current
                    })(),
                    completion: ((): EStepperBulletCompletion => {
                        if (isDataEmpty || m.status === "complete") return EStepperBulletCompletion.Completed

                        return EStepperBulletCompletion.NotCompleted
                    })(),
                    state: EStepperBulletState.Normal,
                    title: _isUnassigned ? t("common.unassigned") : translator(m.name),
                    subTitle: isDataEmpty ? undefined : `Module ${idx + 1}`
                }
            }
        )

        return adaptedEnrollments.length === journeyLength
            ? adaptedEnrollments
            : [
                  ...adaptedEnrollments,
                  ...Array.from(
                      { length: journeyLength - adaptedEnrollments.length },
                      (_: unknown, idx: number): TNavigationBullet => {
                          const _accIdx: number = adaptedEnrollments.length + idx + 1

                          return {
                              isStub: true,
                              href: isDataEmpty
                                  ? undefined
                                  : EPath.Coach_GroupParticipant(
                                        { groupId, participantId },
                                        { tab: "Modules", moduleId: -_accIdx }
                                    ),
                              pageId: -_accIdx,
                              order: ((): EStepperBulletOrder => {
                                  if (isDataEmpty || _accIdx !== Math.abs(moduleId))
                                      return EStepperBulletOrder.NotCurrent

                                  return EStepperBulletOrder.Current
                              })(),
                              completion: EStepperBulletCompletion.NotCompleted,
                              state: EStepperBulletState.Normal,
                              title: t("common.unassigned"),
                              subTitle: isDataEmpty ? undefined : `Module ${_accIdx}`
                          }
                      }
                  )
              ]
    },
    buildModuleListData(queryModel: IModel) {
        if (isEmpty(queryModel)) return []

        type _TChapterWithModuleName = IChapterBaseModel & { moduleName: TranslationObj }

        const allChapters: _TChapterWithModuleName[] = flatMap<IModuleModel, _TChapterWithModuleName>(
            queryModel.results,
            (result: IModuleModel): _TChapterWithModuleName[] =>
                map<IChapterBaseModel, _TChapterWithModuleName>(
                    result.chapters,
                    (chapter: IChapterBaseModel): _TChapterWithModuleName => ({
                        ...chapter,
                        moduleName: result.name
                    })
                )
        )

        const groupedByModuleAndPhase: Record<string, _TChapterWithModuleName[]> = groupBy<_TChapterWithModuleName>(
            allChapters,
            (chapter: _TChapterWithModuleName): string =>
                `${JSON.stringify(chapter.moduleTitle)}-${chapter.phaseOrder}-${chapter.phaseProgressId}`
        )

        return map(values(groupedByModuleAndPhase), (chapters: _TChapterWithModuleName[]) => {
            const firstChapter: _TChapterWithModuleName = head(chapters)

            const chats: Array<{ component: IChatComponentModel; chapter: IChapterBaseModel }> = flatMap(
                chapters,
                (chapter: _TChapterWithModuleName): { component: IChatComponentModel; chapter: IChapterBaseModel }[] =>
                    map<IChatComponentModel, { component: IChatComponentModel; chapter: IChapterBaseModel }>(
                        filter<IChapterComponentModel>(chapter.components, {
                            type: EChapterComponentType.CoachingMoment
                        }) as IChatComponentModel[],
                        (c: IChatComponentModel): { component: IChatComponentModel; chapter: IChapterBaseModel } => ({
                            component: c,
                            chapter
                        })
                    )
            )

            return {
                key: {
                    moduleTitle: firstChapter.moduleTitle,
                    phaseOrder: firstChapter.phaseOrder,
                    phaseName: firstChapter.moduleName,
                    phaseProgressId: firstChapter.phaseProgressId
                },
                chats
            }
        })
    }
}
